/**
 * PHASE 7 — camera/AR controller.
 *
 * Owns the camera lifecycle for the AR overlay: permission flow, stream
 * acquisition, video attachment, cleanup, and visibility handling. The
 * browser surface (getUserMedia, the video element) is injected as small
 * structural interfaces, so the whole state machine is unit-testable with
 * fakes (same pattern as OrientationFusion/OrientationEventLike).
 *
 * The camera is ONLY a visual background. It never feeds the astronomy,
 * sensor, or coordinate pipeline — orientation stays with OrientationFusion.
 */

export type CameraState =
  | "unsupported" // browser/device has no getUserMedia at all
  | "idle" // never requested, or stopped by the user
  | "requesting" // getUserMedia in flight
  | "active" // stream attached, video ready
  | "denied" // permission refused
  | "failed"; // any other stream error (no device, hardware, …)

export interface CameraTrackLike {
  stop(): void;
}

export interface CameraStreamLike {
  getTracks(): readonly CameraTrackLike[];
}

export interface CameraVideoLike {
  srcObject: unknown;
  play(): Promise<void> | void;
  addEventListener(type: string, cb: () => void, options?: { once?: boolean }): void;
  removeEventListener(type: string, cb: () => void): void;
}

export interface CameraProviderLike {
  getUserMedia(constraints?: MediaStreamConstraints): Promise<CameraStreamLike>;
}

export interface CameraControllerOptions {
  /** Defaults to navigator.mediaDevices.getUserMedia when present. */
  provider: CameraProviderLike | null;
  /** The <video> the stream is attached to. */
  video: CameraVideoLike | null;
  /** Wait for video readiness up to this long; 0 = attach and go. */
  readinessTimeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 4000;

export class CameraController {
  private provider: CameraProviderLike | null;
  private video: CameraVideoLike | null;
  private readinessTimeoutMs: number;
  private currentState: CameraState;
  private stream: CameraStreamLike | null = null;
  private listeners = new Set<(state: CameraState) => void>();
  /** Whether the user last asked the camera to be on (drives auto-restart). */
  private desired = false;
  /** Bumped on stop; cancels any in-flight getUserMedia result. */
  private requestSeq = 0;
  private onMetadata: (() => void) | null = null;
  private metadataTimer: ReturnType<typeof setTimeout> | null = null;
  private resolveReadiness: (() => void) | null = null;

  constructor(options: CameraControllerOptions) {
    this.provider = options.provider;
    this.video = options.video;
    this.readinessTimeoutMs = options.readinessTimeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.currentState = this.provider && this.video ? "idle" : "unsupported";
  }

  get state(): CameraState {
    return this.currentState;
  }

  /** True while the live feed is composited under the sky renderer. */
  get active(): boolean {
    return this.currentState === "active";
  }

  onChange(listener: (state: CameraState) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Ensure a stream is live. No-op (no duplicate streams) when already active. */
  async start(): Promise<CameraState> {
    this.desired = true;
    if (this.currentState === "active" || this.currentState === "requesting") return this.currentState;
    if (!this.provider || !this.video) {
      this.setState("unsupported");
      return this.currentState;
    }
    this.setState("requesting");
    const requestId = ++this.requestSeq;
    let stream: CameraStreamLike;
    try {
      stream = await this.provider.getUserMedia({
        video: {
          facingMode: "environment",
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });
    } catch (err) {
      if (requestId !== this.requestSeq) return this.currentState; // stopped meanwhile
      this.desired = false;
      this.setState(isPermissionError(err) ? "denied" : "failed");
      return this.currentState;
    }
    if (requestId !== this.requestSeq) {
      // The camera was stopped while the request was in flight: release it.
      for (const track of stream.getTracks()) track.stop();
      return this.currentState;
    }
    this.stream = stream;
    this.video.srcObject = stream;
    void this.video.play();
    await this.waitForReadiness(this.video);
    if (requestId === this.requestSeq && this.stream === stream) this.setState("active");
    return this.currentState;
  }

  /** Stop every track, release the stream, forget the feed. */
  stop(): void {
    this.desired = false;
    this.release();
  }

  /**
   * Tab/visibility changes: release the camera when hidden (the system may
   * take it back), restart automatically when visible again if the user
   * still wants the camera on.
   */
  handleVisibility(hidden: boolean): void {
    if (hidden) {
      this.release();
    } else if (this.desired && this.currentState !== "active") {
      void this.start();
    }
  }

  /** Release the stream without forgetting the user's intent. */
  private release(): void {
    this.requestSeq++;
    this.clearReadiness();
    if (this.stream) {
      for (const track of this.stream.getTracks()) track.stop();
      this.stream = null;
    }
    if (this.video) this.video.srcObject = null;
    if (this.currentState === "active" || this.currentState === "requesting") {
      this.setState("idle");
    }
  }

  private waitForReadiness(video: CameraVideoLike): Promise<void> {
    this.clearReadiness();
    return new Promise((resolve) => {
      if (this.readinessTimeoutMs <= 0) {
        resolve();
        return;
      }
      this.resolveReadiness = resolve;
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        this.onMetadata = null;
        if (this.metadataTimer) clearTimeout(this.metadataTimer);
        this.metadataTimer = null;
        const r = this.resolveReadiness;
        this.resolveReadiness = null;
        r?.();
      };
      this.onMetadata = finish;
      video.addEventListener("loadedmetadata", finish, { once: true });
      this.metadataTimer = setTimeout(finish, this.readinessTimeoutMs);
    });
  }

  private clearReadiness(): void {
    if (this.onMetadata && this.video) this.video.removeEventListener("loadedmetadata", this.onMetadata);
    this.onMetadata = null;
    if (this.metadataTimer) clearTimeout(this.metadataTimer);
    this.metadataTimer = null;
    // A cancelled wait must still settle so start() can return (it then
    // notices the request was superseded and does not go active).
    const r = this.resolveReadiness;
    this.resolveReadiness = null;
    r?.();
  }

  private setState(next: CameraState): void {
    if (this.currentState === next) return;
    this.currentState = next;
    for (const listener of this.listeners) listener(this.currentState);
  }
}

/** NotAllowedError / PermissionDeniedError indicate a user refusal. */
export function isPermissionError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const name = (err as { name?: unknown }).name;
  return name === "NotAllowedError" || name === "PermissionDeniedError";
}